import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { debounce } from "@s-libs/micro-dash";
import moment from "moment";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { getAssignmentContentIds } from "@/assignments/utils/assignmentUtils";
import logSubmissionError from "@/monitoring/logActivitySubmissionError";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import getRelevantWritePromptResponses from "@/view/utils/getRelevantWritePromptResponses";
import getWritePromptResponsesByCompletionStatus from "@/view/utils/getWritePromptResponsesByCompletionStatus";
import useFocusManager from "@/view/utils/useFocusManager";
import { scrollContainerCss } from "../ActivityPanel/ActivityPanel.style";
import NavigationButtons from "../NavigationButtons";
import getRelevantWritePrompts from "../utils/getRelevantWritePrompts";
import useWritePromptResponseGet from "../utils/useWritePromptResponseGet";
import useWritePromptResponsePost from "../utils/useWritePromptResponsePost";
import WritePromptClassroomSelection from "../WritePromptClassroomSelection";
import WritePromptConfirmationScreen from "../WritePromptConfirmationScreen";
import WritePromptSubmitted from "../WritePromptSubmitted";
import { containerCss } from "./StudentWritePrompt.style";
import StudentWriteResponseForm from "./StudentWriteResponseForm";

const propTypes = {
  classrooms: PropTypes.arrayOf(PropTypes.object),
  classroomListLoading: PropTypes.bool,
  collectionId: PropTypes.number,
  deleteParams: PropTypes.func,
  fetchedWritePrompts: PropTypes.arrayOf(
    PropTypes.shape({
      collections: PropTypes.arrayOf(PropTypes.object),
      id: PropTypes.string,
      teacher: PropTypes.object,
      questions: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  legacyArticleLevelHeaderId: PropTypes.number,
  legacyArticleLevelId: PropTypes.number,
  legacyWritePrompts: PropTypes.array,
  studentId: PropTypes.number,
  assignments: PropTypes.array,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unique_id: PropTypes.string,
  }),
  contentId: PropTypes.string,
  currentLevel: PropTypes.object,
};

const StudentWritePrompt = ({
  classrooms,
  classroomListLoading,
  collectionId,
  deleteParams,
  fetchedWritePrompts,
  legacyArticleLevelHeaderId,
  legacyArticleLevelId,
  studentId,
  assignments,
  contentId,
  currentLevel,
  user,
}) => {
  const router = useRouter();
  const {
    query: { classroomId, levelId, writePromptId },
  } = router;

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isConfirmationScreen, setIsConfirmationScreen] = useState(false);
  const [mostRecentSave, setMostRecentSave] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [selectedClassroom, setSelectedClassroom] = useState({});
  const writePromptConfirmationScreenRef = useRef();
  const writePromptSubmittedClassroomNameRef = useRef();

  const [
    fetchWritePromptResponses,
    { data: writePromptResponses, loading: writePromptResponsesAreLoading },
  ] = useWritePromptResponseGet({
    headerId: legacyArticleLevelHeaderId,
    studentId,
  });
  const [
    createWritePromptResponse,
    {
      loading: createWritePromptResponseLoading,
      error: createWritePromptResponseError,
    },
  ] = useWritePromptResponsePost();

  const updateWritePromptResponseAndRefetch = useCallback(
    async (submissionObject) => {
      const result = await createWritePromptResponse(submissionObject);
      await fetchWritePromptResponses();
      setIsAutoSaving(false);
      return result;
    },
    [fetchWritePromptResponses, createWritePromptResponse],
  );

  useEffect(() => {
    if (createWritePromptResponseError) {
      logSubmissionError({
        message: "Unable to submit Write Prompt response",
        context: {
          activityId: writePromptId,
          contentId,
          currentLevel,
          user,
          assignmentIds: getAssignmentContentIds(assignments),
          url: router.asPath,
        },
      });
    }
  }, [
    createWritePromptResponseError,
    writePromptId,
    contentId,
    currentLevel,
    user,
    assignments,
    router.asPath,
  ]);

  useEffect(() => {
    fetchWritePromptResponses();
  }, []);

  useEffect(() => {
    setMostRecentSave(null);
  }, [legacyArticleLevelId]);

  const getSelectedClassroom = () => {
    return classrooms.find(
      (classroom) => classroom?.id.toString() === classroomId,
    );
  };

  useEffect(() => {
    if (classrooms) {
      setSelectedClassroom(getSelectedClassroom());
    }
  }, [JSON.stringify(classrooms), classroomId]);

  const relevantWritePrompts = useMemo(
    () =>
      getRelevantWritePrompts(
        legacyArticleLevelId,
        selectedClassroom,
        fetchedWritePrompts,
        writePromptResponses,
        collectionId,
      ),
    [
      legacyArticleLevelId,
      selectedClassroom?.id,
      JSON.stringify(fetchedWritePrompts),
      JSON.stringify(writePromptResponses),
      collectionId,
    ],
  );

  const mostRelevantPrompt = relevantWritePrompts?.[0];

  const relevantWriteResponses = useMemo(
    () =>
      getRelevantWritePromptResponses(
        writePromptResponses,
        legacyArticleLevelId,
        mostRelevantPrompt?.id,
        classroomId,
      ),
    [
      JSON.stringify(writePromptResponses),
      legacyArticleLevelId,
      mostRelevantPrompt?.id,
      classroomId,
    ],
  );

  const classroomIsSelected = !!classroomId;
  const completedResponses = getWritePromptResponsesByCompletionStatus(
    relevantWriteResponses,
    true,
  );
  const mostRecentReponse = completedResponses[completedResponses?.length - 1];
  const isReviewed =
    mostRecentReponse?.review && !mostRecentReponse?.review?.score;
  const hasCompletedWriteResponses = completedResponses.length >= 1;
  const draftResponses = getWritePromptResponsesByCompletionStatus(
    relevantWriteResponses,
    false,
  );
  const mostRecentlyDraftedReponse =
    draftResponses?.[draftResponses?.length - 1];

  const { focusElement } = useFocusManager();

  // Focus management based on the url params and the state of the panel
  useEffect(() => {
    if (classroomIsSelected && isConfirmationScreen) {
      writePromptConfirmationScreenRef?.current?.focus();
    } else if (
      classroomIsSelected &&
      !isConfirmationScreen &&
      hasCompletedWriteResponses
    ) {
      writePromptSubmittedClassroomNameRef?.current?.focus();
    } else {
      focusElement(focusElementRefKeys.activityPanelBackButton);
    }
  }, [isConfirmationScreen, hasCompletedWriteResponses]);

  const submitStudentWriteResponse = (completed = false, draftText) => {
    if (responseText.length === 0 && draftText?.length === 0) return;
    const assignmentIds = getAssignmentContentIds(assignments);

    const submissionObject = {
      articleId: legacyArticleLevelId,
      classroomId,
      dateCompleted: completed ? moment().toISOString() : null,
      promptId: mostRelevantPrompt?.id,
      text: draftText ?? responseText,
      assignmentIds,
    };

    if (mostRecentlyDraftedReponse?.id) {
      submissionObject.id = mostRecentlyDraftedReponse?.id;
    }

    if (!submissionObject.dateCompleted && draftText?.length !== 0) {
      setIsAutoSaving(true);
      setMostRecentSave(moment());
    }

    updateWritePromptResponseAndRefetch(submissionObject);
  };

  const autoSave = useCallback(
    debounce((text) => submitStudentWriteResponse(false, text), 2000),
    [
      classroomId,
      legacyArticleLevelId,
      mostRelevantPrompt?.id,
      mostRecentlyDraftedReponse?.id,
    ],
  );

  useEffect(() => {
    setResponseText(mostRecentlyDraftedReponse?.text ?? "");
  }, [mostRecentlyDraftedReponse?.id, legacyArticleLevelId]);

  const loadingWriteResponses =
    createWritePromptResponseLoading || writePromptResponsesAreLoading;
  /**
   * We want to display the LoadingSpinner during GET and POST requests,
   * except during autosaving in the StudentWriteResponseForm, where it would otherwise
   * interrupt the user's ability to enter their input
   */
  if (classroomListLoading || (!isAutoSaving && loadingWriteResponses))
    return <LoadingSpinner />;

  const handleChange = (text) => {
    setResponseText(text);
    autoSave(text);
  };

  const onClickNavigationButtons = (navigateTo) => {
    switch (navigateTo) {
      case "next":
      case "edit":
        setIsConfirmationScreen(!isConfirmationScreen);
        break;
      case "back":
        deleteParams(["classroomId"]);
        break;
      case "submit":
        setIsConfirmationScreen(false);
        autoSave.cancel();
        submitStudentWriteResponse(true);
        break;
    }
  };

  const renderClassroomSelection = () => {
    if (!classrooms) {
      return null;
    }

    return !classroomIsSelected ? (
      <WritePromptClassroomSelection
        classrooms={classrooms}
        collectionId={collectionId}
        fetchedWritePrompts={fetchedWritePrompts}
        legacyArticleLevelId={legacyArticleLevelId}
        responses={writePromptResponses}
      />
    ) : null;
  };

  const renderStudentWriteResponseForm = () => {
    return (classroomIsSelected &&
      !isConfirmationScreen &&
      !hasCompletedWriteResponses) ||
      (!isConfirmationScreen && isReviewed) ? (
      <StudentWriteResponseForm
        classroomName={selectedClassroom?.name}
        currentWritePrompt={mostRelevantPrompt}
        handleChange={handleChange}
        isAutoSaving={isAutoSaving}
        mostRecentSave={mostRecentSave}
        writePromptResponseText={responseText}
        isSubmittedAndReviewed={isReviewed}
      />
    ) : null;
  };

  const renderStudentWritePromptConfirmation = () => {
    return classroomIsSelected && isConfirmationScreen ? (
      <WritePromptConfirmationScreen
        writePromptConfirmationScreenRef={writePromptConfirmationScreenRef}
      />
    ) : null;
  };

  const renderStudentWritePromptSubmitted = () => {
    return classroomIsSelected &&
      !isConfirmationScreen &&
      hasCompletedWriteResponses ? (
      <WritePromptSubmitted
        classroomName={selectedClassroom?.name}
        currentWritePrompt={mostRelevantPrompt}
        writePromptResponses={completedResponses}
        writePromptSubmittedClassroomNameRef={
          writePromptSubmittedClassroomNameRef
        }
      />
    ) : null;
  };

  return (
    <>
      <div css={[containerCss, scrollContainerCss]}>
        {renderClassroomSelection()}
        {renderStudentWritePromptConfirmation()}
        {renderStudentWritePromptSubmitted()}
        {renderStudentWriteResponseForm()}
      </div>
      {classroomIsSelected && (
        <NavigationButtons>
          <NavigationButtons.WritePromptNavigationButtons
            handleClick={onClickNavigationButtons}
            isConfirmationScreen={isConfirmationScreen}
            hasRelevantWriteResponses={hasCompletedWriteResponses}
            isDisabled={responseText.length === 0}
            objectId={writePromptId}
            isSubmittedAndReviewed={isReviewed}
            contentId={levelId}
            // Only show "Back" buttons if there is more than one classroom to choose from
            showBackToClassesButton={classrooms.length > 1}
          />
        </NavigationButtons>
      )}
    </>
  );
};

StudentWritePrompt.propTypes = propTypes;

export default StudentWritePrompt;
